import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Shield, Edit, Settings } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { IconButton } from '@/components/common/IconButton';
import { toast } from 'sonner';
import { roleLabel } from '@/lib/roleLabels';

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const fetchData = async () => {
    setPageLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/admin/roles'),
        api.get('/admin/permissions'),
      ]);
      setRoles(rolesRes.data.data || []);
      setAllPermissions(permsRes.data.data || {});
    } catch (e) { toast.error('Failed to load roles'); }
    finally { setPageLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const togglePerm = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const openEdit = (role) => {
    setEditRole(role);
    setForm({ name: role.name, description: role.description || '', permissions: role.permissions || [] });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditRole(null);
    setForm({ name: '', description: '', permissions: [] });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editRole) {
        await api.put(`/admin/roles/${editRole.id}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/admin/roles', form);
        toast.success('Role created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const permGroups = {};
  Object.entries(allPermissions).forEach(([key, desc]) => {
    const [group] = key.split('.');
    if (!permGroups[group]) permGroups[group] = [];
    permGroups[group].push({ key, desc });
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Roles & Permissions</h1>
          <p className="text-muted-foreground mt-1">Manage access control roles</p>
        </div>
        <Button onClick={openCreate} data-testid="role-create-button"><Plus className="h-4 w-4 mr-2" /> New Role</Button>
      </div>

      {/* KPI band — additive only */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="roles-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="roles-summary-total">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total roles</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{roles.length}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="roles-summary-system">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">System</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-sky-700">{roles.filter(r => r.is_system).length}</div>
        </div>
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="roles-summary-custom">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Custom</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-violet-700">{roles.filter(r => !r.is_system).length}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="roles-summary-permissions">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Permissions defined</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-700">{Object.keys(allPermissions).length}</div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {pageLoading ? (
            <TableSkeleton rows={5} columns={4} testid="roles-loading" />
          ) : roles.length === 0 ? (
            <EmptyState
              icon={Settings}
              title="No roles configured yet"
              description="Create your first role to define what users can see and do."
              action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-1" />New Role</Button>}
              testid="roles-empty"
            />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Permissions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.id} className="hover:bg-muted/60" data-testid={`role-row-${r.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      {roleLabel(r.name)}
                      {roleLabel(r.name) !== r.name && <span className="text-xs text-muted-foreground">({r.name})</span>}
                      {r.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.permissions?.length || 0}</TableCell>
                  <TableCell>
                    <IconButton label="Edit role" onClick={() => openEdit(r)} testid={`role-edit-${r.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>Configure role name and permissions</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="role-name-input" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            </div>
            <div className="space-y-4">
              <Label>Permissions ({form.permissions.length} selected)</Label>
              {Object.entries(permGroups).map(([group, perms]) => (
                <div key={group} className="border rounded-lg p-3">
                  <p className="text-sm font-medium capitalize mb-2">{group}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {perms.map(({ key, desc }) => (
                      <label key={key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1">
                        <Checkbox checked={form.permissions.includes(key)} onCheckedChange={() => togglePerm(key)} />
                        <span className="text-muted-foreground">{desc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="role-submit-button">
              {loading ? 'Saving...' : editRole ? 'Update Role' : 'Create Role'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
